import { useTheme } from '@emotion/react';
import { endOfMonth, formatISO, isAfter, isValid, parseISO, startOfDay } from 'date-fns';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { Box, Flex, Text } from '~/components/blocks';

import { FieldError } from '../FieldError';
import { Select } from '../Select';

const MAX_YEAR = new Date().getFullYear();
const MIN_YEAR = 1903; // 2021年1月時点で世界最高齢（日本人）の方が1903年生まれのため

type Props = {
  error?: string;
  disabled?: boolean;
  value: string;
  onChange: (date: string) => void;
};

export type InputBirthdateAttr = {
  reset: () => void;
};

type Option = {
  label: string;
  value: number;
};

const MONTHS = Array.from({ length: 12 }).map<Option>((_, idx) => ({
  label: `${idx + 1}`,
  value: idx + 1,
}));

const makeYearOptions = (maxYear: number, minYear: number) => {
  let year = maxYear;
  const options: Option[] = [];
  for (; year >= minYear; year--) {
    options.push({
      label: `${year}`,
      value: year,
    });
  }
  return options;
};

/**
 * year/monthが不正な値だった場合は31を返す
 * @param year
 * @param month
 */
const buildDayOptions = (year: '' | number, month: '' | number) => {
  let lastDay = 31;
  const today = startOfDay(new Date());

  if (year && month) {
    const dateStr = `${year}-${`${month}`.padStart(2, '0')}-01`;
    if (isValid(parseISO(dateStr))) {
      const targetDate = new Date(dateStr);
      if (isAfter(targetDate, today)) {
        return [];
      }
      lastDay = endOfMonth(targetDate).getDate();
    }
  }

  if (year === today.getFullYear() && month === today.getMonth() + 1) {
    lastDay = Math.min(lastDay, today.getDate());
  }

  return Array.from({ length: lastDay }).map<Option>((_, idx) => ({
    label: `${idx + 1}`,
    value: idx + 1,
  }));
};

export const InputBirthdate = forwardRef<InputBirthdateAttr, Props>((props: Props, ref) => {
  const { disabled, error, onChange } = props;

  const theme = useTheme();

  const [year, setYear] = useState<'' | number>('');
  const [month, setMonth] = useState<'' | number>('');
  const [day, setDay] = useState<'' | number>('');
  const yearOptions = useMemo(() => makeYearOptions(MAX_YEAR, MIN_YEAR), []);
  const [dayOptions, setDayOptions] = useState<Option[]>(buildDayOptions(year, month));

  const handleYearChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => setYear(+e.target.value),
    [],
  );
  const handleMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => setMonth(+e.target.value),
    [],
  );
  const handleDayChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => setDay(+e.target.value),
    [],
  );

  useImperativeHandle(ref, () => ({
    reset: () => {
      setDay('');
      setMonth('');
      setYear('');
    },
  }));

  useEffect(() => {
    const _dayOptions = buildDayOptions(year, month);
    setDayOptions(_dayOptions);

    if (year && month && day) {
      const lastDay = _dayOptions[_dayOptions.length - 1]?.value;
      if (!lastDay) {
        return;
      }

      const dayNumber = day > lastDay ? lastDay : day;
      const dateStr = `${`${year}`.padStart(4, '0')}-${`${month}`.padStart(
        2,
        '0',
      )}-${`${dayNumber}`.padStart(2, '0')}`;

      if (isValid(parseISO(dateStr))) {
        const selectedDate = startOfDay(parseISO(dateStr));
        const today = startOfDay(new Date());

        // 未来日が選択された場合は今日の日付に補正
        if (isAfter(selectedDate, today)) {
          onChange(formatISO(today, { representation: 'date' }));
          return;
        }
        onChange(dateStr);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, month, year]);

  useEffect(() => {
    if (isValid(parseISO(props.value))) {
      const _date = new Date(props.value);
      setYear(_date.getFullYear());
      setMonth(_date.getMonth() + 1);
      setDay(_date.getDate());
    }
  }, [props.value]);

  useEffect(() => {
    if (props.value.length === 0) {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [props.value.length]);

  return (
    <Box>
      <Flex marginTop={theme.space.m} alignItems="flex-end" width="100%">
        <Select fill error={error} disabled={disabled} value={year} onChange={handleYearChange}>
          <option disabled hidden value="">
            ー
          </option>
          {yearOptions.map((y) => (
            <option key={y.value} value={y.value}>
              {y.label}
            </option>
          ))}
        </Select>
        <Text marginLeft={theme.space.m} marginRight={theme.space.m} size="m">
          年
        </Text>
        <Select fill error={error} disabled={disabled} value={month} onChange={handleMonthChange}>
          <option disabled hidden value="">
            ー
          </option>
          {MONTHS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </Select>
        <Text marginLeft={theme.space.m} marginRight={theme.space.m} size="m">
          月
        </Text>
        <Select
          fill
          error={error}
          marginLeft={theme.space.m}
          disabled={disabled}
          value={day}
          onChange={handleDayChange}
        >
          <option disabled hidden value="">
            ー
          </option>
          {dayOptions.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </Select>
        <Text marginLeft={theme.space.m} marginRight={theme.space.m} size="m">
          日
        </Text>
      </Flex>
      {error && <FieldError error={error} />}
    </Box>
  );
});

InputBirthdate.displayName = 'InputBirthdate';
